import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  SampleRequestGroup,
  SampleRequestGroupItemsSortColumn,
  SampleRequestGroupsResponse,
  SampleRequestGroupsSortColumn,
  SampleRequestItem,
  SampleRequestItemsResponse,
  SampleRequestStats,
  SampleRequestTabCounts,
} from "@unbox/api";
import { SAMPLES_NEGATABLE_FILTERS } from "@unbox/api";
import { handleError } from "@/api/error";
import { type Pagination, toQuery } from "@/api/pagination";
import { toNotQuery } from "@/providers/filter-registry";
import type { SAMPLE_REQUEST_TABS, SAMPLE_REQUEST_VIEWS } from "@/router";
import { client, getScopedMerchantClient, useScopedMerchantClient } from "./scoped-merchant-client";

export type SampleRequestView = (typeof SAMPLE_REQUEST_VIEWS)[number];
export type SampleRequestTab = (typeof SAMPLE_REQUEST_TABS)[number];
export type SampleRequestGroupSort = { column: SampleRequestGroupsSortColumn; direction: "asc" | "desc" };
export type SampleRequestGroupItemSort = { column: SampleRequestGroupItemsSortColumn; direction: "asc" | "desc" };
export type {
  SampleRequestGroup,
  SampleRequestGroupsResponse,
  SampleRequestItem,
  SampleRequestItemsResponse,
  SampleRequestStats,
  SampleRequestTabCounts,
};

export type SampleRequestFilters = {
  productIds?: string[];
  collaborationIds?: string[];
  brandSafety?: ("low" | "high")[];
  minPostRate?: number;
  maxPostRate?: number;
  not?: string[];
};

type GroupParams = {
  view: SampleRequestView;
  tab: SampleRequestTab;
  search?: string;
  sort?: SampleRequestGroupSort;
  filters?: SampleRequestFilters;
};
type ItemParams = {
  view: SampleRequestView;
  tab: SampleRequestTab;
  groupId: string;
  sort?: SampleRequestGroupItemSort;
  filters?: SampleRequestFilters;
};

// IMPORTANT: the filter fields below must appear identically in BOTH the queryKey and the request
// query builder. A drift between them produces silent cache mismatches (wrong rows, no error).
const filtersToQuery = (filters?: SampleRequestFilters) => ({
  ...(filters?.productIds?.length ? { productIds: filters.productIds } : {}),
  ...(filters?.collaborationIds?.length ? { collaborationIds: filters.collaborationIds } : {}),
  ...(filters?.brandSafety?.length ? { brandSafety: filters.brandSafety } : {}),
  ...(filters?.minPostRate !== undefined ? { minPostRate: filters.minPostRate.toString() } : {}),
  ...(filters?.maxPostRate !== undefined ? { maxPostRate: filters.maxPostRate.toString() } : {}),
  not: toNotQuery(filters?.not, SAMPLES_NEGATABLE_FILTERS),
});

const filtersToKey = (filters?: SampleRequestFilters) =>
  [
    filters?.productIds?.join(","),
    filters?.collaborationIds?.join(","),
    filters?.brandSafety?.join(","),
    filters?.minPostRate,
    filters?.maxPostRate,
    filters?.not?.join(","),
  ] as const;

// IMPORTANT: every key is prefixed with "sample-requests" so approve/reject/ignore
// mutations can invalidate the whole feature with one prefix match.

export const useSampleRequestGroups = (params: GroupParams, pagination: Pagination) => {
  const scope = useScopedMerchantClient();
  return useQuery({
    queryKey: [
      "sample-requests",
      "groups",
      scope.merchantId,
      scope.workspaceId,
      params.view,
      params.tab,
      params.search,
      params.sort?.column,
      params.sort?.direction,
      ...filtersToKey(params.filters),
      pagination.limit,
      pagination.offset,
    ] as const,
    queryFn: async () => {
      const { param, workspaceId } = getScopedMerchantClient(scope.merchantId, scope.workspaceId);
      const query = {
        ...toQuery(pagination),
        view: params.view,
        tab: params.tab,
        ...(params.search ? { search: params.search } : {}),
        ...(params.sort ? { column: params.sort.column, direction: params.sort.direction } : {}),
        ...filtersToQuery(params.filters),
      };
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.groups.$get({ param, query })
        : await client.merchant[":merchantId"].samples.groups.$get({
            param,
            query,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useSampleRequestGroupItems = (params: ItemParams, pagination: Pagination, enabled = true) => {
  const scope = useScopedMerchantClient();
  return useQuery({
    queryKey: [
      "sample-requests",
      "group-items",
      scope.merchantId,
      scope.workspaceId,
      params.view,
      params.tab,
      params.groupId,
      params.sort?.column,
      params.sort?.direction,
      ...filtersToKey(params.filters),
      pagination.limit,
      pagination.offset,
    ] as const,
    queryFn: async () => {
      const { param, workspaceId } = getScopedMerchantClient(scope.merchantId, scope.workspaceId);
      const query = {
        ...toQuery(pagination),
        view: params.view,
        tab: params.tab,
        groupId: params.groupId,
        ...(params.sort ? { column: params.sort.column, direction: params.sort.direction } : {}),
        ...filtersToQuery(params.filters),
      };
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples["group-items"].$get({ param, query })
        : await client.merchant[":merchantId"].samples["group-items"].$get({
            param,
            query,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: enabled && !!scope.merchantId && !!params.groupId,
  });
};

export const useSampleAllocations = (params?: { search?: string }) => {
  const scope = useScopedMerchantClient();
  return useQuery({
    queryKey: ["sample-requests", "allocations", scope.merchantId, scope.workspaceId, params?.search] as const,
    queryFn: async () => {
      const { param, workspaceId } = getScopedMerchantClient(scope.merchantId, scope.workspaceId);
      const query = params?.search ? { search: params.search } : {};
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.allocations.$get({ param, query })
        : await client.merchant[":merchantId"].samples.allocations.$get({ param, query });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useSampleRequestTabCounts = () => {
  const scope = useScopedMerchantClient();
  return useQuery({
    queryKey: ["sample-requests", "tab-counts", scope.merchantId, scope.workspaceId] as const,
    queryFn: async () => {
      const { param, workspaceId } = getScopedMerchantClient(scope.merchantId, scope.workspaceId);
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples["tab-counts"].$get({ param })
        : await client.merchant[":merchantId"].samples["tab-counts"].$get({
            param,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useSampleRequestStats = () => {
  const scope = useScopedMerchantClient();
  return useQuery({
    queryKey: ["sample-requests", "stats", scope.merchantId, scope.workspaceId] as const,
    queryFn: async () => {
      const { param, workspaceId } = getScopedMerchantClient(scope.merchantId, scope.workspaceId);
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.stats.$get({ param })
        : await client.merchant[":merchantId"].samples.stats.$get({ param });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useSetSampleAllocations = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (allocations: { productId: string; monthlyCap: number }[]) => {
      if (!scope.merchantId) throw new Error("No merchant selected");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.allocations.$put({
            param: scope.param,
            json: { allocations },
          })
        : await client.merchant[":merchantId"].samples.allocations.$put({
            param: scope.param,
            json: { allocations },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sample-requests"] });
    },
  });
};

export const useIgnoreSampleRequest = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (applicationId: string) => {
      if (!scope.merchantId) throw new Error("No merchant selected");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples[":applicationId"].ignore.$post({
            param: { ...scope.param, applicationId },
          })
        : await client.merchant[":merchantId"].samples[":applicationId"].ignore.$post({
            param: { ...scope.param, applicationId },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sample-requests"] });
    },
  });
};
